import { forkJoin as observableForkJoin, Subscription } from 'rxjs';
import { map, mergeMap, first } from 'rxjs/operators';
import { AddUserDialogComponent } from './addUserDialog.component';
import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import * as _ from 'lodash';
import { ArrayDataSource } from '../ui/arrayDataSource';
import { StreamService } from '../services/stream.service';
import { TenantService } from '../services/tenant.service';
import { AddProductsDialogComponent } from './addProductsDialog.component';
import { ConfirmationDialogComponent } from '../general/confirmationDialog.component';

@Component({
    selector: 'edit-site',
    styles: [`
        .too-many-subscribers {
            color: #ff0000
        }
        mat-icon.warning-icon {
            padding-left: 3px;
        }
        .edit-control {
            padding: 0px;
            max-width: 25px;
            min-width: 25px;
        }
    `],
    template: `
            <mat-toolbar>
                <button mat-icon-button (click)="back()" matTooltip="Back to Site List">
                    <mat-icon aria-label="Back to List">keyboard_backspace</mat-icon>
                </button>
                <span fxFlexOffset="20px">{{tenant?.name}}</span>
                <span fxFlex></span>
                <button mat-icon-button (click)="startAddUser()" matTooltip="Add User">
                    <mat-icon aria-label="Edit Site">person_add</mat-icon>
                </button>
                <button mat-icon-button *ngIf="!isEditing" (click)="isEditing = true;" matTooltip="Edit Site Details">
                    <mat-icon aria-label="Edit Site">mode_edit</mat-icon>
                </button>
            </mat-toolbar>
            <div fxLayout="column" *ngIf="!isEditing && !noTouching">
                <mat-card>
                    <mat-card-title>
                        <span>Products</span>
                        <span fxFlex></span>
                        <button mat-button (click)="startAddingProducts()">Add Product</button>
                    </mat-card-title>
                    <mat-card-content>
                        <div *ngIf="!productsDataSource" fxLayoutAlign="center center">
                            <spinner></spinner>                    
                        </div>
                        <mat-table *ngIf="productsDataSource" #table [dataSource]="productsDataSource">
                            <ng-container matColumnDef="name">
                                <mat-header-cell *matHeaderCellDef>Name</mat-header-cell>
                                <mat-cell *matCellDef="let row" style="overflow: hidden; white-space: nowrap;">
                                    <div style="text-overflow: ellipsis; overflow: hidden;" [matTooltip]="row.title">{{row.title}}</div>
                                </mat-cell>
                            </ng-container>
                            <ng-container matColumnDef="followers">
                                <mat-header-cell *matHeaderCellDef>Followers</mat-header-cell>
                                <mat-cell *matCellDef="let row"><span>{{row.followersCount}}</span></mat-cell>
                            </ng-container>
                            <ng-container matColumnDef="subscribers">
                                <mat-header-cell *matHeaderCellDef>Subscribers</mat-header-cell>
                                <mat-cell *matCellDef="let row">
                                    <div *ngIf="row.streamSubscribersCount > row.maxStreamSubscribers" 
                                          class="too-many-subscribers"
                                          fxLayoutAlign="start center"
                                          [matTooltip]="overfollowedMessage(row)">
                                        {{row.streamSubscribersCount}} <mat-icon class="warning-icon">error_outline</mat-icon>
                                    </div>
                                    <span *ngIf="row.maxStreamSubscribers === undefined || row.streamSubscribersCount <= row.maxStreamSubscribers"
                                          [matTooltip]="followersMessage(row)">{{row.streamSubscribersCount}}</span>
                                </mat-cell>
                            </ng-container>
                            <ng-container matColumnDef="maxStreamSubscribers">
                                <mat-header-cell *matHeaderCellDef>Maximum Subscribers</mat-header-cell>
                                <mat-cell *matCellDef="let row">
                                    <div *ngIf="row.originalMaxStreamSubscribers !== undefined">
                                        <form #maxForm="ngForm">
                                            <mat-form-field style="max-width: 50px">
                                                <input matInput 
                                                       name="maxStreamSubscribers"
                                                       type="number"
                                                       min="0"
                                                       minNumber="0"
                                                       #maxStreamSubscribers="ngModel"
                                                       [placeholder]="row.maxStreamSubscribers || row.maxStreamSubscribers === 0 ? '' : 'unlimited'" 
                                                       [(ngModel)]="row.maxStreamSubscribers">
                                                <mat-error *ngIf="maxStreamSubscribers.hasError('minNumber')">must be 0 or greater or empty</mat-error>
                                            </mat-form-field>
                                            <button mat-button
                                                    class="edit-control" 
                                                    [disabled]="maxForm.invalid || row.maxStreamSubscribers === row.originalMaxStreamSubscribers" 
                                                    (click)="row.originalMaxStreamSubscribers = undefined; updateProduct(row);">
                                                <mat-icon>check</mat-icon>
                                            </button>
                                            <button mat-button 
                                                    class="edit-control" 
                                                    (click)="row.maxStreamSubscribers = row.originalMaxStreamSubscribers; row.originalMaxStreamSubscribers = undefined;">
                                                <mat-icon>cancel</mat-icon>
                                            </button>
                                        </form>
                                    </div>
                                    <div *ngIf="row.originalMaxStreamSubscribers === undefined">
                                        <div *ngIf="row.streamSubscribersCount > row.maxStreamSubscribers" 
                                            class="too-many-subscribers"
                                            fxLayoutAlign="start center"
                                            [matTooltip]="overfollowedMessage(row)">
                                            <button mat-button (click)="row.originalMaxStreamSubscribers = row.maxStreamSubscribers;">{{row.maxStreamSubscribers}}</button> 
                                            <mat-icon class="warning-icon">error_outline</mat-icon>
                                        </div>
                                        <span *ngIf="row.maxStreamSubscribers === undefined || row.streamSubscribersCount <= row.maxStreamSubscribers"
                                              [matTooltip]="followersMessage(row)">
                                            <button mat-button (click)="row.originalMaxStreamSubscribers = row.maxStreamSubscribers || -1;">
                                                {{!row.maxStreamSubscribers && row.maxStreamSubscribers !== 0? 'unlimited' : row.maxStreamSubscribers}}
                                            </button>
                                        </span>
                                    </div>
                                </mat-cell>
                            </ng-container>
                            <ng-container matColumnDef="actions">
                                <mat-header-cell *matHeaderCellDef></mat-header-cell>
                                <mat-cell *matCellDef="let row" fxLayoutAlign="end center">
                                    <button mat-button (click)="deleteProduct(row)"><mat-icon>close</mat-icon></button>
                                </mat-cell>
                            </ng-container>
                            <mat-header-row *matHeaderRowDef="columns"></mat-header-row>
                            <mat-row *matRowDef="let row; columns: columns;"></mat-row>
                        </mat-table>
                    </mat-card-content>
                </mat-card>
            </div>
            <div fxLayout="column" *ngIf="isEditing">
                <tenant-editor *ngIf="tenant" [productCount]="productCount" [tenant]="tenant" (tenantSaved)="done($event)" (cancelled)="done()"></tenant-editor>
            </div>
    `
})
export class SiteDetailsComponent implements OnDestroy {
    subscriptions: Array<Subscription> = [];
    isEditing: boolean = false;
    noTouching: boolean = true;
    tenant: any;
    limitProductSources: boolean;
    columns = [];
    productCount: number;
    productsDataSource: ArrayDataSource;

    constructor(private router: Router, 
                private route: ActivatedRoute,
                private dialog: MatDialog,
                private streamService: StreamService,
                private tenantService: TenantService) {
        this.subscriptions.push(this.route.params.subscribe((params: Params) => {
            this.tenantService.getSubtenant(params.subtenantId).pipe(first())
            .subscribe(tenant => {
                this.tenant = tenant;
                this.configureForTenant();
            });
        }));
    }

    configureForTenant = () => {
        this.limitProductSources = this.tenant.settings.limitProductSources === 'true';
        this.columns = this.limitProductSources ? 
                       ['name', 'subscribers', 'maxStreamSubscribers', 'actions'] : 
                       ['name', 'followers', 'actions'];
        this.fetchProducts();
        this.noTouching = false;
    }

    fetchProducts = () => {
        this.productsDataSource = undefined;
        this.streamService.getSubtenantStreams(this.tenant.id, {type: 'PRODUCT_SOURCE', rows: 200 }).pipe(first())
            .subscribe(streamResults => {
                this.productCount = streamResults.content ? streamResults.content.length: 0;
                this.productsDataSource = new ArrayDataSource(streamResults.content)
            });
    }

    followersMessage = product => `${product.streamSubscribersCount} registered users of ${product.maxStreamSubscribers} allowed`;

    overfollowedMessage = product => `This product has reached the maximum of ${product.maxStreamSubscribers} users.`;

    createProduct = (parentStream: { id, 
                                     title,
                                     description, 
                                     largeImage, 
                                     largeImageId, 
                                     largeImageAttachmentId,
                                     thumbnailImage,
                                     thumbnailImageAttachmentId,
                                     thumbnailImageId }, maxStreamSubscribers: number) => this.streamService.saveSubtenantStream(this.tenant.id, {
            title: parentStream.title,
            type: 'PRODUCT_SOURCE',
            description: parentStream.description,
            openForReading: !this.limitProductSources,
            published: true,
            maxStreamSubscribers: maxStreamSubscribers,
            largeImageAttachmentId: parentStream.largeImageAttachmentId,
            thumbnailImageAttachmentId: parentStream.thumbnailImageAttachmentId,
            parentSourceStreamId: parentStream.id,
            settings: { itemTimeToLiveInDays: 90 }
        })
        .pipe(mergeMap(createdStream =>  this.streamService.subscribeStream(createdStream.id, parentStream.id, this.tenant.id)));

    startAddingProducts = () => this.productsDataSource.connect()
    .pipe(
        map(productArray => productArray.map((stream: any) => stream.parentSourceStreamId)),
        mergeMap(currentProductIds => this.dialog.open(AddProductsDialogComponent, { 
            width: '560px', 
            data: { ids: currentProductIds, limitProductSources: this.limitProductSources } 
        }).afterClosed()), first()
    ).subscribe(addables => {
        if (addables) {
            this.productsDataSource = undefined;
            observableForkJoin(addables.map(addable => this.createProduct(addable.stream, addable.maxStreamSubscribers))).pipe(first()).subscribe(() => this.fetchProducts());
        }
    }); 

    updateProduct = product => this.streamService.updateSubtenantStream(product).pipe(first()).subscribe(() => {}, err => console.log('errorz', err));

    deleteProduct = product => this.dialog.open(ConfirmationDialogComponent, { data: {title: `Delete ${product.title}?`, actionLabel: 'Delete'}}).afterClosed().pipe(first())
    .subscribe(doIt => {
        if (doIt) {
            this.streamService.deleteSubtenantStream(this.tenant.id, product.id).pipe(first()).subscribe(() => this.fetchProducts());
        } 
    });

    done = tenant => {
        this.tenant = tenant || this.tenant;
        this.configureForTenant();
        this.isEditing = false;
    };

    back = () => this.isEditing ? this.isEditing = false : this.router.navigate(['settings', 'partner', 'sites', 'list'], { queryParams: this.route.snapshot.queryParams});

    startAddUser = () => {
        this.dialog.open(AddUserDialogComponent, { 
            data: {
                hostname: this.tenant.hostName,
                tenantId: this.tenant.id
            }, width: '500px'}).afterClosed().pipe(first())
            .subscribe(results => console.log('should we do something if there are results?'));
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}